import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Download, Loader2, Smartphone } from 'lucide-react';
import Pagination from "@/components/ui/PaginationWrapper";

function RepPopupNew({ text, closePopup }) {
    const [activeTab, setActiveTab] = useState('Active');
    const [activePage, setActivePage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [mainAPIResult, setMainAPIResult] = useState([]);
    const [error, setError] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const ITEMS_PER_PAGE = 6;

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    // Detect mobile screen
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const fetchRepsData = useCallback(async () => {
        if (!userdata) return;
        
        setLoading(true);
        setError('');
        
        try {
            const response = await fetch('/api/ws/repsNew', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'X-Browser-Url': window.location.href 
                },
                body: JSON.stringify({
                    Prefix: '',
                    WhName: userdata.wholesaler,
                    wholesaler_Id: userdata.wholesaler_Id
                })
            });
            
            const data = await response.json();
            
            if (data?.length > 0) {
                setMainAPIResult(data);
            }
        } catch (err) {
            setError('Something went wrong. Please try later.');
        } finally {
            setLoading(false);
        }
    }, [userdata]);

    useEffect(() => {
        fetchRepsData();
    }, [fetchRepsData]);

    const { activeJson, inactiveJson, totalLen } = useMemo(() => {
        try {
            if (mainAPIResult.length > 0) {
                const active = JSON.parse(mainAPIResult[0].activeJson);
                const inactive = JSON.parse(mainAPIResult[0].inActiveJson);
                return {
                    activeJson: active,
                    inactiveJson: inactive,
                    totalLen: active.length + inactive.length
                };
            }
        } catch {
            // ignore parse errors
        }
        return { activeJson: [], inactiveJson: [], totalLen: 0 };
    }, [mainAPIResult]);

    const currentList = activeTab === 'Active' ? activeJson : inactiveJson;
    
    const paginatedList = useMemo(() => {
        if (currentList.length <= ITEMS_PER_PAGE) return currentList;
        const startIndex = (activePage - 1) * ITEMS_PER_PAGE;
        return currentList.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [currentList, activePage]);

    const handleTabChange = useCallback((value) => {
        setActiveTab(value);
        setActivePage(1);
    }, []);

    const handlePageChange = useCallback((pageNumber) => {
        setActivePage(pageNumber);
    }, []);

    const handleExportReps = useCallback((e) => {
        e.preventDefault();
        if (!userdata) return;
        
        try {
            const originLocation = window.location.protocol + '//' + window.location.host;
            window.open(
                `${originLocation}/report/exportreps1?&objectuid=${userdata.id}`,
                '_blank'
            );
        } catch (error) {
        }
    }, [userdata]);

    const handleOpenChange = useCallback((open) => {
        if (!open) {
            closePopup();
        }
    }, [closePopup]);

    const renderRepRow = (item, index) => (
        <TableRow key={`rep-${index}`}>
            <TableCell className="font-medium">{item.DisName}</TableCell>
            <TableCell>{item.DisEmail}</TableCell>
            <TableCell>
                {item.GroupList?.map((gpitem, gIndex) => (
                    <div key={`group-${gIndex}`}>
                        {gpitem.Group?.split(' ')[0]}
                    </div>
                ))}
            </TableCell>
            <TableCell>
                {item.GroupList?.map((gpitem, gIndex) => (
                    <div key={`comm-${gIndex}`}>
                        {gpitem.Group?.split('(')[1]?.replace(')', '')}
                    </div>
                ))}
            </TableCell>
        </TableRow>
    );

    return (
        <Dialog open={true} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-4xl max-h-[90vh]">
                <DialogHeader>
                    <DialogTitle>{text}</DialogTitle>
                </DialogHeader>

                <div className="flex flex-col gap-4">
                    {loading ? (
                        <div className="flex items-center justify-center p-8 min-h-[120px]">
                            <Loader2 className="h-8 w-8 animate-spin text-primary" />
                            <p className="mt-2 text-muted-foreground">Please wait...</p>
                        </div>
                    ) : isMobile ? (
                        <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                            <Smartphone className="h-16 w-16 text-muted-foreground mb-4" />
                            <h3 className="text-lg font-semibold text-foreground mb-2">
                                Content Not Available
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                Content not available for mobile screen. Please use a desktop or tablet device.
                            </p>
                        </div>
                    ) : totalLen === 0 ? (
                        <div className="flex items-center justify-center p-8 text-muted-foreground text-center text-[15px]">
                            <p>No Reps Found</p>
                        </div>
                    ) : (
                        <Tabs value={activeTab} onValueChange={handleTabChange}>
                            <div className="flex flex-col sm:flex-row sm:items-start gap-4 mb-4">
                                <div className="flex-1">
                                    <h3 className="text-lg font-semibold">
                                        {activeTab === 'Active' 
                                            ? `Total Active Reps: ${activeJson.length}`
                                            : `Total Inactive Reps: ${inactiveJson.length}`
                                        }
                                    </h3>
                                    <p className="text-sm text-muted-foreground">
                                        Reps with {activeTab === 'Active' ? 'greater' : 'less'} than 1 claim in last 30 days
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <TabsList className="w-full sm:w-auto">
                                        <TabsTrigger value="Active">Active</TabsTrigger>
                                        <TabsTrigger value="Inactive">Inactive</TabsTrigger>
                                    </TabsList>
                                    <Button
                                        variant="secondary"
                                        size="icon"
                                        onClick={handleExportReps}
                                        title="Download report"
                                        className="h-8 w-auto px-2"
                                    >
                                        <Download className="h-5 w-5" /> Export
                                    </Button>
                                </div>
                            </div>

                            <TabsContent value={activeTab} className="mt-0">
                                {currentList.length > 0 ? (
                                    <>
                                        <div className="rounded-md border max-h-[300px] overflow-auto">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>Name</TableHead>
                                                        <TableHead>E-mail</TableHead>
                                                        <TableHead>Assigned Group</TableHead>
                                                        <TableHead>Commission</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {paginatedList.map(renderRepRow)}
                                                </TableBody>
                                            </Table>
                                        </div>
                                        {currentList.length > ITEMS_PER_PAGE && (
                                            <div className="mt-4">
                                                <Pagination
                                                    activePage={activePage}
                                                    itemsCountPerPage={ITEMS_PER_PAGE}
                                                    totalItemsCount={currentList.length}
                                                    pageRangeDisplayed={5}
                                                    onChange={handlePageChange}
                                                />
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <p className="text-center text-muted-foreground py-4">
                                        No {activeTab.toLowerCase()} reps found
                                    </p>
                                )}
                            </TabsContent>

                            {error && (
                                <div className="mt-4 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                                    {error}
                                </div>
                            )}
                        </Tabs>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default RepPopupNew;
